import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ForgeAutocomplete, ForgeTextField } from '@tylertech/forge-react';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { AutocompleteDropdownConfig } from '../config/filter-config';
import { TAB_MY_TEAM_ASSETS } from 'common/components/AssetBrowser/lib/constants';
import styles from './autocomplete-dropdown-filter.module.scss';
import { selectAssetBrowserFilterOptions } from 'common/components/AssetBrowser/selectors/filters';

/**
 * Temporary State for Asset Selector tab state, as that file is in JS at the moment
 */
type TempTabState = { header: { activeTab: string } };

export const AutocompleteDropdownFilter = ({
  label,
  placeholder,
  filterCallback,
  filterListName,
  dispatchAction,
  filterName,
  transformSelection,
}: AutocompleteDropdownConfig<unknown>) => {
  const dispatch: ThunkDispatch<any, unknown, AnyAction> = useDispatch();

  const selectFilter = useMemo(
    () => selectAssetBrowserFilterOptions<object | string>(filterListName),
    [filterListName],
  );
  const list = useSelector(selectFilter);

  /**
   * showTeamsOnly is for Owned By filter only.
   * When on "My team assets" tab, only show list of teams
   */
  const showTeamsOnly = useSelector(
    (reduxState: TempTabState) => reduxState.header.activeTab === TAB_MY_TEAM_ASSETS,
  );

  const filter = useCallback(
    (input: string) => filterCallback(input, list, showTeamsOnly),
    [filterCallback, list, showTeamsOnly],
  );

  const onChange = (selectedOption: string | null): void => {
    const transform = transformSelection ?? ((option) => option);
    const transformedSelection = transform(selectedOption, list);

    dispatch(dispatchAction(transformedSelection));
  };

  return (
    <ForgeAutocomplete
      className={styles['autocomplete-dropdown-filter']}
      filter={filter}
      on-forge-autocomplete-change={(e: CustomEvent<string | null>) => onChange(e.detail)}
      allowUnmatched={false}
      data-testid={`autocomplete-dropdown-${filterName}`}
    >
      <ForgeTextField label-position="block-start" showClear={true} variant="filled">
        <label htmlFor={`autocomplete-dropdown-${filterName}`}>{label}</label>
        <input
          id={`autocomplete-dropdown-${filterName}`}
          placeholder={placeholder}
          data-testid={`autocomplete-input-${filterName}`}
        />
      </ForgeTextField>
    </ForgeAutocomplete>
  );
};
